import React, { Fragment, useEffect, useState } from 'react';
import { css, styled } from 'styled-components';
import { imageWidths } from './image-width';
import { useCoreConfig } from '../../../../play-core/config/CoreConfigContext';
import { useInitialRender } from '../../../../play-core/hooks/use-initial-render';
import { usePrevious } from '../../../../play-core/hooks/use-previous';
import { largeRange, mediumRange, smallRange, xLargeRange } from '../../../../play-core/styles/Viewports';
import { scaleImageSource } from '../../../../play-core/utils/image-helper';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const imageSrc = (src, width, imageFormat, modernImagesBaseUrl) => scaleImageSource(src, width, imageFormat, modernImagesBaseUrl);
const getSizes = (width, smallWidth, mediumWidth, largeWidth, xLargeWidth) => [smallWidth && `(max-width: ${smallRange.to}px) ${smallWidth}px`, mediumWidth && `(max-width: ${mediumRange.to}px) ${mediumWidth}px`, largeWidth && `(max-width: ${largeRange.to}px) ${largeWidth}px`, xLargeWidth && `(min-width: ${xLargeRange.from}px) ${xLargeWidth}px`, `${width}px`].filter(condition => !!condition).join(', ');
export const DensityAwareImage = ({
  src,
  width,
  smallImageWidth,
  mediumImageWidth,
  largeImageWidth,
  xLargeImageWidth,
  hasBorderRadius = true,
  className,
  priorize = false,
  focalPoint = {
    percentageX: 50,
    percentageY: 50
  },
  objectFit = 'cover',
  ...rest
}) => {
  const isInitialRender = useInitialRender();
  const [isImageBroken, setImageBroken] = useState(false);
  const previousSrc = usePrevious(src);
  const {
    modernImagesBaseUrl
  } = useCoreConfig();
  useEffect(() => {
    if (previousSrc && previousSrc !== src) {
      setImageBroken(false);
    }
  }, [src, previousSrc]);
  const imageProps = imageFormat => {
    if (priorize) return {
      src: imageSrc(src, width, imageFormat, modernImagesBaseUrl),
      sizes: getSizes(width, smallImageWidth, mediumImageWidth, largeImageWidth, xLargeImageWidth),
      srcSet: getSourceSet(src, imageFormat, modernImagesBaseUrl)
    };
    // lazysizes behaves different on server and client (classname & data attribute srcset), therefore we skip initial render
    if (!isInitialRender) {
      return {
        'data-src': imageSrc(src, width, imageFormat, modernImagesBaseUrl),
        sizes: getSizes(width, smallImageWidth, mediumImageWidth, largeImageWidth, xLargeImageWidth),
        'data-srcset': getSourceSet(src, imageFormat, modernImagesBaseUrl)
      };
    }
  };
  return /*#__PURE__*/_jsx(Fragment, {
    children: !isImageBroken && /*#__PURE__*/_jsxs("picture", {
      className: className,
      children: [modernImagesBaseUrl && /*#__PURE__*/_jsx(Source, {
        hasBorderRadius: hasBorderRadius,
        "aria-hidden": "true",
        priorize: priorize,
        type: 'image/webp',
        focalPoint: focalPoint,
        objectFit: objectFit,
        ...imageProps('webp')
      }), /*#__PURE__*/_jsx(Image, {
        hasBorderRadius: hasBorderRadius,
        "aria-hidden": "true",
        className: `${!priorize && !isInitialRender && 'lazyload'}`,
        onError: () => setImageBroken(true),
        width: width,
        priorize: priorize,
        focalPoint: focalPoint,
        objectFit: objectFit,
        ...imageProps('jpg'),
        ...rest
      })]
    })
  });
};
const imageStyle = css`
  opacity: ${({
  priorize
}) => priorize ? 1 : 0};
  filter: blur(${({
  priorize
}) => priorize ? 0 : '1rem'});
  border-radius: ${props => props.hasBorderRadius ? '2px' : '0px'};
  transition:
    opacity 0.1s ease-in,
    filter 0.1s ease-in;
  object-fit: ${({
  objectFit
}) => objectFit};
  object-position: ${props => getObjectPosition(props.focalPoint)};
  width: 100%;
  height: 100%;

  &.lazyloaded {
    opacity: 1;
    filter: blur(0);
  }
`;
const Image = styled.img.withConfig({
  displayName: "DensityAwareImage__Image",
  componentId: "sc-19my7mj-0"
})(["", ";"], imageStyle);
const Source = styled.source.withConfig({
  displayName: "DensityAwareImage__Source",
  componentId: "sc-19my7mj-1"
})(["", ";"], imageStyle);
const getSourceSet = (src, imageFormat, useModernImage) => `${imageWidths.map(size => `${scaleImageSource(src, size, imageFormat, useModernImage)} ${size}w`).join(', ')}`;
const getObjectPosition = focalPoint => {
  const x = focalPoint.percentageX * 2 - 50;
  const y = focalPoint.percentageY * 2 - 50;
  return `${normToPercentage(x)}% ${normToPercentage(y)}%`;
};
const normToPercentage = value => {
  if (value > 100) {
    return 100;
  }
  if (value < 0) {
    return 0;
  }
  return value;
};